import React, { FC, useEffect, useState, Fragment } from 'react';
import injectSheet from 'react-jss';
import styleSheet from './styles';
import Table from 'HOC/Table';
import RadioButton from 'Components/RadioButton';
import Animations from '../../../animations';
import { Loading } from 'Components/Loading';
import {
  DEFAULT_CELL_WIDTH,
  COLUMNS,
  SELECT_COLUMN_CONFIG,
} from '../constants';
import { numberWithCommas } from 'Utils/numberWithCommas';
import { FormattedDate } from 'Utils/formattedDate';
import { motion } from 'framer-motion';

interface Props {
  classes?: any;
  audienceData: any;
  tableColumns: string[];
  allColumns: string[];
  error?: any;
  loading?: any;
  onChange?: (e: object) => void;
  sort?: any;
  isChecked?: any;
  columnConfig?: any;
  defaultCellWidth?: string;
  columns?: any;
  changeCols?: any;
}

const SelectList: FC<Props> = props => {
  const [exportSettings, setExportSettings] = useState(false);
  const [tableWidth, setTableWidth] = useState(null);

  const renderHeaders = (
    tableColumns: string[],
    columnConfig: any,
    defaultCellWidth: string,
    sort?: any
  ) =>
    tableColumns.map((key: string, index: number) => {
      // Tables aren't templated, therefore custom padding needs to be implemented to meet design spec //
      const colConfigAttr = columnConfig[key],
        isSortable = (colConfigAttr && colConfigAttr.isSortable) || null,
        title = (colConfigAttr && colConfigAttr.title) || 'undefined',
        width = (colConfigAttr && colConfigAttr.width) || defaultCellWidth,
        sortKey = (colConfigAttr && colConfigAttr.sortKey) || undefined,
        doSort = isSortable && sortKey && sort;

      if (doSort) {
        //Add the sortKey if sort capabilities are being added to a column
        sort['sortKey'] = sortKey;
      }

      switch (title) {
        case 'List Name':
          return (
            <Table.Cell
              caret={isSortable}
              minWidth={width}
              key={index}
              sort={doSort ? { ...sort } : null}
            >
              <span style={{ paddingLeft: '15px' }}>{title}</span>
            </Table.Cell>
          );
        default:
          return (
            <Table.Cell
              caret={isSortable}
              minWidth={width}
              key={index}
              sort={doSort ? { ...sort } : null}
            >
              {title}
            </Table.Cell>
          );
      }
    });

  const renderCell = (
    columnConfig: any,
    columns: any,
    defaultCellWidth: string,
    cellKey: string,
    value: any,
    classes: any,
    callBacks?: any
  ) => {
    const column = columnConfig[cellKey];
    const cellWidth = column && column.width ? column.width : defaultCellWidth;

    switch (cellKey) {
      case columns.CHECKBOX:
        return (
          <Table.Cell key={cellKey} minWidth={cellWidth}>
            <div className={classes.checkBoxHolder}>
              <RadioButton
                margin="0"
                isChecked={props.isChecked.id === value}
                onClick={props.onChange}
                id={value}
                value={value}
              />
            </div>
          </Table.Cell>
        );
      case columns.NAME:
        return (
          <Table.Cell key={cellKey} minWidth={cellWidth}>
            <div className={classes.firstCell}>{value}</div>
          </Table.Cell>
        );
      case columns.ATTRIBUTES:
        return (
          <Table.Cell key={cellKey} minWidth={cellWidth}>
            <div className={classes.firstCell}>{value}</div>
          </Table.Cell>
        );
      case columns.LEADS:
        return (
          <Table.Cell key={cellKey} minWidth={cellWidth}>
            <div className={classes.firstCell}>{numberWithCommas(value)}</div>
          </Table.Cell>
        );
      case columns.LAST_MODIFIED:
        return (
          <Table.Cell key={cellKey} minWidth={cellWidth}>
            {FormattedDate(value)}
          </Table.Cell>
        );
      default:
        return (
          <Table.Cell key={cellKey} minWidth={cellWidth}>
            {value}
          </Table.Cell>
        );
    }
  };

  const onRowClick = (lead: any) => {
    let e = { target: { lead: lead } };
    props.onChange ? props.onChange(e) : null;
  };

  const renderRows = (
    columnConfig: any,
    columns: any,
    defaultCellWidth: string,
    list: any,
    tableColumns: string[],
    classes: any,
    callBacks?: any
  ) =>
    list.map((data: any, index: number) => {
      return (
        <motion.div
          initial={{ zIndex: 0, boxShadow: '0 2px 8px 0 rgba(0,0,0,0)' }}
          transition={{ duration: 0.2 }}
          whileHover={{ zIndex: 10, boxShadow: '0 2px 8px 0 rgba(0,0,0,0.15)' }}
        >
          <Table.Row
            onClick={() => {
              onRowClick(data);
            }}
            key={index}
            padding="12px 24px"
            justifyContent={
              tableColumns.length === 6 ? 'space-around' : 'normal'
            }
            color="#051D39"
          >
            {renderCell(
              columnConfig,
              columns,
              defaultCellWidth,
              'checkbox',
              data.id,
              classes,
              callBacks
            )}
            {tableColumns.map((key: string) =>
              renderCell(
                columnConfig,
                columns,
                defaultCellWidth,
                key,
                data[key],
                classes
              )
            )}
          </Table.Row>
        </motion.div>
      );
    });

  function handleClickOutside(event: any) {
    if (exportSettings && !event.target.closest('#settingsContainer')) {
      setExportSettings(false);
    }
  }

  useEffect(() => {}, [props.audienceData]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  /*
      Check to see if a Column config was passed in.  If so, use it.   
      Otherwise, use the local constants imported at the top.
      This not only allows for different configurations for the table, it allows us to use knobs to configure
      this data in storybook for better testing

      This would be a good candidate for lazy loading
    */
  const columnConfig = props.columnConfig || SELECT_COLUMN_CONFIG,
    defaultCellWidth = props.defaultCellWidth || DEFAULT_CELL_WIDTH,
    columns = props.columns || COLUMNS,
    isChecked = props.isChecked;

  /* In the Table Heder Row, Tables aren't templated, therefore custom padding needs to be implemented to meet design spec */
  if (!props.loading && !props.error)
    return (
      <div className={props.classes.container}>
        <Table padding="3px" overflowx="auto">
          <Table.Header changeTableWidth={(width: any) => setTableWidth(width)}>
            <Table.Row
              padding={'22px 24px 22px 38px'}
              justifyContent={
                props.tableColumns.length === 6 ? 'space-around' : 'normal'
              }
            >
              {renderHeaders(
                props.tableColumns,
                columnConfig,
                defaultCellWidth,
                props.sort
              )}
            </Table.Row>
          </Table.Header>

          <div className={props.classes.bodyBackground}>
            {props.audienceData.list &&
            props.audienceData.list.length > 0 &&
            !props.loading &&
            !props.error ? (
              <Table.Body>
                {renderRows(
                  columnConfig,
                  columns,
                  defaultCellWidth,
                  props.audienceData.list,
                  props.tableColumns,
                  props.classes
                )}
              </Table.Body>
            ) : (
              <div className={props.classes.noResults}>
                <span>[No Results]</span>
              </div>
            )}
          </div>
        </Table>
      </div>
    );
  else
    return (
      <Fragment>
        {props.loading && <Loading />}
        {props.error && <div>{props.error}</div>}
      </Fragment>
    );
};

export default injectSheet(styleSheet)(SelectList);
